/**
 * @author: subhasish.ghosh@zoom.us
 */

import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  ButtonBase,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import InputAdornment from "@mui/material/InputAdornment";
import OutlinedInput from "@mui/material/OutlinedInput";
import { Button, Switch } from "@zoom/react-ui";
import map from "lodash/map";
import React, { useEffect, useState } from "react";
import "react-resizable/css/styles.css";
import {
  APPROVER_LEVELS,
  DISABLE_DECLINE_CANCELLATION_ACTION_TYPES,
  TABLE_TYPES_KEYS,
  STATUS_OPTIONS,
} from "@utils/constants";
import {
  downloadTableExportFile,
  formatDateString,
  generateTableExportFilename,
  getDataGridSortOrderForPayload,
  getOneYearAgo,
  getToday,
  isSelectedActionTypeNonPaymentDirect,
} from "@utils/utils";
import "./styles.css";
import { useSnackbar } from "notistack";
import axiosInstance from "@utils/axiosInstance";
import DateRangePicker from "./DateRangePicker";
import { DATE_FORMAT } from "@utils/timeUnits";
import flags from "@flags";

const approverLevelItems =
  map(APPROVER_LEVELS, (value, key) => {
    return { label: value, key: key };
  }) || [];

export const PendingSearchBar = ({
  isRecordSelected,
  isApproveEligible,
  searchTerm,
  setSearchTerm,
  setApproveAction,
  setApprovalRejectionModalOpen,
  showAll,
  setShowAll,
  hideL1Approved,
  setHideL1Approved,
  approverLevel,
  setApproverLevel,
  selectedActionType,
  sortOrder,
  pagination,
  hasPendingTData,
  isExactlyOneSelected,
  setExecutionDecline,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-action/export`,
        {
          actionType: selectedActionType,
          searchTerm: searchTerm,
          showAll: showAll,
          ...(showAll && { hideL1Status: hideL1Approved }),
          ...(!showAll &&
            isSelectedActionTypeNonPaymentDirect(selectedActionType) && {
              approverLevel: approverLevel,
            }),
          sort: getDataGridSortOrderForPayload(sortOrder, TABLE_TYPES_KEYS[0]),
          offset: pagination.page + 1,
          limit: pagination.pageSize,
        },
        {
          responseType: "blob",
        }
      );

      // download export
      downloadTableExportFile(
        generateTableExportFilename(selectedActionType, TABLE_TYPES_KEYS[0]),
        response
      );
    } catch (err) {
      console.error("Export failed:", err);
      enqueueSnackbar(
        "Failed to export Pending Cancellation data. Please try again.",
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="searchbar-container">
      <Box sx={{ display: "grid" }}>
        <Typography className="show-all-text">Show All</Typography>
        <ButtonBase onClick={() => setShowAll((old) => !old)}>
          <Switch size="small" checked={showAll} />
        </ButtonBase>
      </Box>
      {showAll ? (
        <Box sx={{ display: "grid" }}>
          <Typography className="show-all-text" style={{ minWidth: "110px" }}>
            Hide L1 Approved
          </Typography>
          <ButtonBase onClick={() => setHideL1Approved((old) => !old)}>
            <Switch size="small" checked={hideL1Approved} />
          </ButtonBase>
        </Box>
      ) : isSelectedActionTypeNonPaymentDirect(selectedActionType) ? (
        <Box sx={{ display: "grid" }}>
          <FormControl
            sx={{ margin: "8px 0", padding: 1, width: 150, display: "flex" }}
            size="small"
          >
            <InputLabel
              id="select_label"
              shrink={true}
              className="select_label"
            >
              Show Approver Data
            </InputLabel>
            <Select
              labelId="select_label"
              id="select_label"
              value={approverLevel}
              label="approver_level_select&"
              onChange={(e) => {
                setApproverLevel(e.target.value);
              }}
              displayEmpty
            >
              {approverLevelItems.map(({ label, key }) => (
                <MenuItem key={key} value={key}>
                  <ListItemText primary={label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : (
        <></>
      )}
      <Box
        sx={{ display: "flex", flexWrap: "wrap" }}
        className="search_input_container"
      >
        <FormControl
          fullWidth
          sx={{ m: 1 }}
          className="aging-table-search-form"
        >
          <InputLabel htmlFor="outlined-adornment-amount">Search</InputLabel>
          <OutlinedInput
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            label="Search"
            value={searchTerm}
            id="searchbar-search-input"
            className="search_input"
            onChange={(e) => {
              e.preventDefault();
              setSearchTerm(e.target.value);
            }}
          />
        </FormControl>
      </Box>
      <Button
        id="searchbar-approve-button"
        type="primary"
        size="large"
        className="apprv_rjct_btn"
        onClick={() => {
          setApproveAction(true);
          setApprovalRejectionModalOpen(true);
        }}
        disabled={!isRecordSelected || !isApproveEligible}
      >
        Approve Cancellation(s)
      </Button>
      {DISABLE_DECLINE_CANCELLATION_ACTION_TYPES.includes(
        selectedActionType) && (
        <Button
          id="searchbar-cancellation-extension-button"
          size="large"
          className="apprv_rjct_btn"
          onClick={() => {
            setApproveAction(false);
            setExecutionDecline(true);
            setApprovalRejectionModalOpen(true);
          }}
          disabled={!isExactlyOneSelected}
          sx={{ marginRight: "15px" }}
          danger
        >
          Defer Cancellation
        </Button>
     )}
      {!DISABLE_DECLINE_CANCELLATION_ACTION_TYPES.includes(
        selectedActionType
      ) ? (
        <Button
          id="searchbar-reject-button"
          size="large"
          className="apprv_rjct_btn"
          onClick={() => {
            setApproveAction(false);
            setApprovalRejectionModalOpen(true);
          }}
          disabled={!isRecordSelected}
          danger
        >
          Decline Cancellation(s)
        </Button>
      ) : (
        <></>
      )}
      <Box sx={{ display: "grid", width: "275px" }}>
        <Button
          className="cancellation-table-export-btn"
          type="primary"
          size="large"
          onClick={handleExport}
          disabled={loading || !hasPendingTData}
          sx={{ width: "100%" }}
          loading={loading}
        >
          {loading ? "" : "Export"}
        </Button>
      </Box>
    </div>
  );
};

export const HistoricalSearchBar = ({
  historicalSearchTerm,
  setHistoricalSearchTerm,
  showUserApproved,
  setShowUserApproved,
  dateRangeFilter,
  setDateRangeFilter,
  selectedActionType,
  sortOrder,
  pagination,
  hasHistoricalTData,
  statusFilters,
  setStatusFilters,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    setLoading(true);

    try {
      const response = await axiosInstance.post(
        `/operationmonitor/api/v1/workflow-history/export`,
        {
          actionType: selectedActionType,
          onlyUserApproved: showUserApproved,
          sort: getDataGridSortOrderForPayload(sortOrder, TABLE_TYPES_KEYS[1]),
          searchTerm: historicalSearchTerm,
          startDate: formatDateString(dateRangeFilter?.[0], true),
          endDate: formatDateString(dateRangeFilter?.[1], false),
          offset: pagination.page + 1,
          limit: pagination.pageSize,
          status: statusFilters,
        },
        {
          responseType: "blob",
        }
      );

      // download export
      downloadTableExportFile(
        generateTableExportFilename(selectedActionType, TABLE_TYPES_KEYS[1]),
        response
      );
    } catch (err) {
      console.error("Export failed:", err);
      enqueueSnackbar(
        "Failed to export Historical Cancellation data. Please try again.",
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  };

  // reset date range on action type change
  useEffect(() => {
    const startDate = getOneYearAgo().format(DATE_FORMAT);
    const endDate = getToday().format(DATE_FORMAT);
    const range = [startDate, endDate];
    setDateRangeFilter(range);
  }, [selectedActionType]);

  return (
    <div className="searchbar-container">
      <Box sx={{ display: "grid" }}>
        <Typography className="show-all-text" style={{ minWidth: "90px" }}>
          User Approved
        </Typography>
        <ButtonBase onClick={() => setShowUserApproved((old) => !old)}>
          <Switch size="small" checked={showUserApproved} />
        </ButtonBase>
      </Box>
      <Box>
        <FormControl
          size="small"
          sx={{ margin: "8px 0", padding: 1, width: 280, display: "flex" }}
          className="aging-table-search-form"
          variant="outlined"
        >
          <InputLabel
            htmlFor="outlined-adornment-amount"
            id="status-multi-label"
            shrink={true}
            className="select_label"
          >
            Status
          </InputLabel>
          <Select
            labelId="status-multi-label"
            multiple
            value={statusFilters}
            id="searchbar-search-input"
            className="search_input"
            onChange={(e) => {
              const selected =
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value;

              // Prevent empty selection
              if (selected.length === 0) {
                enqueueSnackbar("Select at least one status.", {
                  variant: "warning",
                });
                return;
              }

              setStatusFilters(selected);
            }}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => (selected || []).join(", ")}
            displayEmpty
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 2500,
                  borderRadius: 2,
                  bgcolor: "white",
                  boxShadow: 4,
                  "& .MuiMenuItem-root": {
                    fontSize: "16px",
                    fontWeight: 400,
                    padding: "1px 1px !important",
                    "&.Mui-selected": {
                      backgroundColor: "white !important",
                    },
                    "&:hover": {
                      backgroundColor: "transparent !important",
                    },
                  },
                },
              },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>
                <Checkbox
                  checked={statusFilters.indexOf(opt) > -1}
                  size="small"
                />
                <ListItemText primary={opt} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ display: "flex", width: "500px", gap: "1px" }}>
        <DateRangePicker
          onDateChange={(date) =>
            setDateRangeFilter([
              date?.startDate?.format(DATE_FORMAT) ||
                getOneYearAgo().format(DATE_FORMAT),
              date?.endDate?.format(DATE_FORMAT) ||
                getToday().format(DATE_FORMAT),
            ])
          }
        />
      </Box>
      <Box
        sx={{ display: "flex", flexWrap: "wrap" }}
        className="search_input_container"
      >
        <FormControl
          fullWidth
          sx={{ m: 1 }}
          className="aging-table-search-form"
        >
          <InputLabel htmlFor="outlined-adornment-amount">Search</InputLabel>
          <OutlinedInput
            startAdornment={
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            }
            label="Search"
            value={historicalSearchTerm}
            id="searchbar-search-input"
            className="search_input"
            onChange={(e) => {
              e.preventDefault();
              setHistoricalSearchTerm(e.target.value);
            }}
          />
        </FormControl>
      </Box>
      <Box sx={{ display: "grid", width: "275px" }}>
        <Button
          className="cancellation-table-export-btn"
          type="primary"
          size="large"
          onClick={handleExport}
          disabled={loading || !hasHistoricalTData}
          sx={{ width: "100%" }}
          loading={loading}
        >
          {loading ? "" : "Export"}
        </Button>
      </Box>
    </div>
  );
};
